import { useEditor, EditorContent, BubbleMenu } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TextAlign from "@tiptap/extension-text-align";
import { useEffect } from "react";
import {
  TextB, TextItalic, TextUnderline, TextH, ListBullets, ListNumbers,
  TextAlignLeft, TextAlignCenter, TextAlignRight, TextAlignJustify,
  ArrowCounterClockwise, ArrowClockwise, LinkSimple, Quotes,
} from "@phosphor-icons/react";

/**
 * RichTextEditor — TipTap wrapper com toolbar profissional.
 * Props:
 *  - content: HTML inicial
 *  - onChange: (html) => void
 *  - editable: bool
 */
export default function RichTextEditor({ content, onChange, editable = true, testid = "rich-editor" }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        link: { openOnClick: false, autolink: true },
      }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
    ],
    content: content || "",
    editable,
    onUpdate: ({ editor }) => onChange && onChange(editor.getHTML()),
  });

  // Sincroniza mudanças externas do prop `content` (ex.: quando IA aplica sugestão)
  useEffect(() => {
    if (!editor) return;
    if (typeof content === "string" && content !== editor.getHTML()) {
      editor.commands.setContent(content, false);
    }
  }, [content, editor]);

  if (!editor) return <div className="text-[#94A3B8] p-4">Carregando editor…</div>;

  const btn = (active, onClick, Icon, title) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`p-2 rounded hover:bg-[#334155] transition-colors ${active ? "bg-[#F59E0B]/20 text-[#F59E0B]" : "text-[#94A3B8]"}`}
    >
      <Icon size={16} weight="duotone" />
    </button>
  );

  const setLink = () => {
    const url = window.prompt("URL do link:", editor.getAttributes("link").href || "");
    if (url === null) return;
    if (url === "") { editor.chain().focus().unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <div className="tce-card overflow-hidden flex flex-col h-full" data-testid={testid}>
      {editable && (
        <div className="flex flex-wrap gap-1 px-3 py-2 border-b border-[#334155] bg-[#1E293B] sticky top-0 z-10">
          {btn(editor.isActive("bold"), () => editor.chain().focus().toggleBold().run(), TextB, "Negrito (Ctrl+B)")}
          {btn(editor.isActive("italic"), () => editor.chain().focus().toggleItalic().run(), TextItalic, "Itálico (Ctrl+I)")}
          {btn(editor.isActive("underline"), () => editor.chain().focus().toggleUnderline().run(), TextUnderline, "Sublinhado (Ctrl+U)")}
          <span className="border-l border-[#334155] mx-1" />
          <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                  className={`px-2 py-1 rounded text-xs font-semibold ${editor.isActive("heading", { level: 1 }) ? "bg-[#F59E0B]/20 text-[#F59E0B]" : "text-[#94A3B8] hover:bg-[#334155]"}`}
                  title="Título 1">H1</button>
          <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                  className={`px-2 py-1 rounded text-xs font-semibold ${editor.isActive("heading", { level: 2 }) ? "bg-[#F59E0B]/20 text-[#F59E0B]" : "text-[#94A3B8] hover:bg-[#334155]"}`}
                  title="Título 2">H2</button>
          <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                  className={`px-2 py-1 rounded text-xs font-semibold ${editor.isActive("heading", { level: 3 }) ? "bg-[#F59E0B]/20 text-[#F59E0B]" : "text-[#94A3B8] hover:bg-[#334155]"}`}
                  title="Título 3">H3</button>
          <span className="border-l border-[#334155] mx-1" />
          {btn(editor.isActive("bulletList"), () => editor.chain().focus().toggleBulletList().run(), ListBullets, "Lista com marcadores")}
          {btn(editor.isActive("orderedList"), () => editor.chain().focus().toggleOrderedList().run(), ListNumbers, "Lista numerada")}
          {btn(editor.isActive("blockquote"), () => editor.chain().focus().toggleBlockquote().run(), Quotes, "Citação")}
          <span className="border-l border-[#334155] mx-1" />
          {btn(editor.isActive({ textAlign: "left" }), () => editor.chain().focus().setTextAlign("left").run(), TextAlignLeft, "Alinhar à esquerda")}
          {btn(editor.isActive({ textAlign: "center" }), () => editor.chain().focus().setTextAlign("center").run(), TextAlignCenter, "Centralizar")}
          {btn(editor.isActive({ textAlign: "right" }), () => editor.chain().focus().setTextAlign("right").run(), TextAlignRight, "Alinhar à direita")}
          {btn(editor.isActive({ textAlign: "justify" }), () => editor.chain().focus().setTextAlign("justify").run(), TextAlignJustify, "Justificar")}
          <span className="border-l border-[#334155] mx-1" />
          {btn(editor.isActive("link"), setLink, LinkSimple, "Inserir/editar link")}
          <span className="border-l border-[#334155] mx-1" />
          {btn(false, () => editor.chain().focus().undo().run(), ArrowCounterClockwise, "Desfazer (Ctrl+Z)")}
          {btn(false, () => editor.chain().focus().redo().run(), ArrowClockwise, "Refazer (Ctrl+Shift+Z)")}
        </div>
      )}
      <div className="flex-1 overflow-y-auto bg-white text-[#0F172A]" translate="no">
        <EditorContent editor={editor} className="doc-editor min-h-[600px] px-12 py-10 max-w-[820px] mx-auto shadow-inner" translate="no" />
      </div>
    </div>
  );
}
